import { logger } from '@adornis/base/logging.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID, subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { countWaitingByWorkshop } from '@adornis/buchungsheld-db/api/participation.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import {
  createLecturerComment,
  deleteLecturerCommentByID,
  LecturerComment,
  subscribeLecturerCommentsByLecturerIDAndInstitutionID,
} from '@adornis/buchungsheld-db/entities/lecturerComments.js';
import { getLecturerDataByLecturerID, LecturerData } from '@adornis/buchungsheld-db/entities/lecturerData.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { dataviewEmptyState, productPrice } from '@adornis/buchungsheld-db/global-helpers.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-data-view-table.js';
import '@adornis/buchungsheld-theme/components/tr-data-view.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/cms/client/generated-form.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import type { XInput } from '@adornis/forms/x-input.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { format } from 'date-fns';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { until } from 'lit/directives/until.js';
import { DateTime } from 'luxon';
import { combineLatest, filter, of, switchMap } from 'rxjs';
import '../../../../../packages/theme/fonts/fonts';
import '../../components/bh-lecturer-data-form';
import { BHLecturerDataForm } from '../../components/bh-lecturer-data-form.js';
import '../../components/bh-stammdaten-form';
import '../profile/bh-page-profile-contracts-lecturer.js';

/**
 * @element bh-page-admin-einrichtung-lecturer-detail
 */
@customElement('bh-page-admin-einrichtung-lecturer-detail')
export class BHPageAdminEinrichtungLecturerDetail extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);
  @property({ attribute: 'lecturer-id' }) lecturerID = new RXController<string | null>(this, null);

  @state() private readonly _lecturerCommentsController = new RXController(
    this,
    combineLatest(this.lecturerID.observable, this.institutionID.observable).pipe(
      filter(Boolean),
      switchMap(([lecturerID, institutionID]) =>
        subscribeLecturerCommentsByLecturerIDAndInstitutionID(
          lecturerID ?? '',
          institutionID ?? '',
        )(selectionSet(() => LecturerComment, 1)),
      ),
    ),
  );

  @state() private readonly _lecturerData = new RXController(
    this,
    this.lecturerID.observable.pipe(
      filter(Boolean),
      switchMap(async lecturerID => getLecturerDataByLecturerID(lecturerID)(selectionSet(() => LecturerData, 3))),
    ),
  );

  //TODO: refactor this to ignore participations, suspensions, care for
  @state() user = new RXController(
    this,
    this.lecturerID.observable.pipe(
      switchMap(userID => (!userID ? of(null) : subscribeByID(User, userID)(selectionSet(() => User, 2)))),
    ),
  );

  @state() private readonly lecturerDataFormRef = createRef<BHLecturerDataForm>();
  @state() private readonly userController = new GenericFieldFormController(this, this.user.observable, {
    success: async doc => {
      try {
        await TrDialog.waitFor(
          doc.save().then(() => this.lecturerDataFormRef.value?.lecturerDataController.validate()),
        );
        void XSnackbar.show('Benutzer wurde erfolgreich gespeichert.');
      } catch (err) {
        logger.error({ err }, 'There was a problem saving the user.');
        void TrDialog.alert('Es gab ein Problem beim Speichern der Nutzerdaten. Bitte versuchen Sie es später erneut.');
      }
    },
    error: error => XSnackbar.show(`${this._translation.translate('error')}: ${error.message}`),
    disabledFields: { createdAt: 1, email: 1 },
  });

  override render() {
    if (this.user.isLoading || this._lecturerData.isLoading) return html` <x-loading-state></x-loading-state> `;

    return html`
      <tr-flex space="md">
        <bh-breadcrumb
          .items=${[
            {
              content: this._translation.translate('SIDEBAR', 'lecturers'),
              active: false,
              href: `/admin/institution-details/${this.institutionID.value}/lecturers`,
            },
            { content: this._translation.translate('user', 'details'), active: true },
          ]}
          .additionalAction=${html`
            <x-flex horizontal space="md">
              <tr-button-small
                icon="send"
                mode="outline"
                .action=${() => {
                  const email = this.user.value?.email;
                  if (!email) return;
                  goTo(`mailto:${email}`, { target: '_blank' });
                }}
              >
                <tr-h4> ${this._translation.translate('user', 'write_mail')} </tr-h4>
              </tr-button-small>
              <tr-button-small
                icon="save"
                .action=${async () => {
                  if (!this.userController.document) return;
                  await this.userController.validate();
                }}
              >
                <tr-h4 bold> ${this._translation.translate('save')} </tr-h4>
              </tr-button-small>
            </x-flex>
          `}
        ></bh-breadcrumb>

        <x-flex space="lg">
          <generated-form
            placeholder-mode="static-floating"
            .formController=${this.userController}
            .whitelist=${['firstName', 'lastName', 'email', 'createdAt']}
          ></generated-form>

          <!-- Lecturer Data-->
          <bh-lecturer-data-form
            ${ref(this.lecturerDataFormRef)}
            .lecturerData=${this._lecturerData.value}
            lecturer-id=${this.lecturerID.value}
          ></bh-lecturer-data-form>
        </x-flex>

        <x-hr></x-hr>

        <tr-h3> ${this._translation.translate('lecturer', 'eventsHeld')} </tr-h3>
        <x-flex space="md"> ${this.renderTeachingTable(false)} ${this.renderTeachingTable(true)} </x-flex>

        <x-hr></x-hr>
        ${this.renderContracts()}

        <x-hr></x-hr>
        <!-- Comments section-->
        <tr-h3> ${this._translation.translate('lecturer', 'comments')} </tr-h3>
        ${this._lecturerComments(this.lecturerID.value ?? '', this.institutionID.value ?? '')}
      </tr-flex>
    `;
  }

  //region events

  renderTeachingTable(showFinished: Boolean) {
    const workshopFilter = new BHFilter({
      lecturerID: this.lecturerID.value,
      aspects: [],
      searchFields: ['name', 'description'],
      sortingDirections: ['asc'],
      sortingFields: ['firstAppointment'],
    });

    const headingTranslationKey = showFinished ? 'finishedPlural' : 'currentPlural';

    // Build filter based on whether the event is already over
    // To that end: max(appointments -> end -> date) < currentTime
    // -> Prob do this in the subscription, but we need to save the info if before or after in the filter I guess?
    // render the dataView
    // columns: chronologically sorted by first appointment, institution

    const { dataViewOptions, filterOptions } = buildFullOptions({
      class: Workshop,
      operation: showFinished ? 'subscribeFinishedWorkshopsForLecturer' : 'subscribeCurrentWorkshopsForLecturer',
      isSortable: true,
      isColumnSelectable: true,
      emptyState: () => dataviewEmptyState({}),
      view: new View({ filter: workshopFilter }),
      customColumns: [
        {
          fieldName: 'institutionID',
          isSortable: true,
          columnName: 'Institution',
          columnWidth: '10ch',
          renderItem: doc => {
            const selection = {
              _id: 1,
              name: 1,
            } as const satisfies BaseQLSelectionSet<Institution>;
            const institution = getByID(Institution, doc.institutionID)(selection);
            return html`${until(
              institution.then(inst => {
                return html`<x-text>${inst?.name}</x-text>`;
              }),
            )}`;
          },
        },
        { fieldName: 'name', isSortable: true, columnName: 'Name', columnWidth: '36ch' },
        { fieldName: 'category.name', columnName: 'Kategorie', columnWidth: '10ch' },
        {
          // This fieldname because we need a fieldname that is already present in the Workshop object
          fieldName: 'appointments',
          isSortable: true,
          columnName: 'Start',
          columnWidth: '10ch',
          renderItem: doc => {
            const appointmentStarts = doc.appointments.map(appointment => appointment.start?.toMillis() ?? NaN);
            const firstAppointment = Math.min(...appointmentStarts);
            const firstAppointmentDateTime = DateTime.fromMillis(firstAppointment);
            return html`<x-text>${firstAppointmentDateTime.toLocaleString()}</x-text>`;
          },
        },
        {
          columnName: 'Preis',
          fieldName: 'price',
          isSortable: true,
          columnWidth: 'max-content',
          selectionSet: { hasPrice: 1, prices: { _id: 1, unitAmount: 1 } },
          renderItem: instance => html` <tr-text>${productPrice(instance)}</tr-text> `,
        },
        {
          columnName: '#TN',
          fieldName: 'participants',
          columnWidth: 'max-content',
          selectionSet: { capacity: 1 },
          renderItem: doc => {
            const countIn = countWaitingByWorkshop(doc._id)();
            return html`${until(
              countIn.then(count => {
                return html` <x-text ${css({ textAlign: 'center' })}> ${count} von ${doc.capacity} </x-text> `;
              }),
              html`<x-text>${this._translation.translate('loading')}...</x-text>`,
            )}`;
          },
        },
        {
          columnName: 'Wartend',
          customID: 'numWaiting',
          columnWidth: 'max-content',
          renderItem: doc => {
            const countWaiting = countWaitingByWorkshop(doc._id)();
            return html`${until(
              countWaiting.then(count => {
                return html` <x-text ${css({ textAlign: 'center' })}> ${count} </x-text> `;
              }),
              html`<x-text>${this._translation.translate('loading')}...</x-text>`,
            )}`;
          },
        },
        {
          columnName: '',
          customID: '_options',
          renderItem: doc =>
            html`
              <x-flex space="xs" horizontal crossaxis-start>
                <tr-button-small
                  icon="more_horiz"
                  .action=${() => goTo(`/admin/institution-details/${doc.institutionID}/workshops/modify/${doc._id}`)}
                  .color=${this.colors.secondary.shade(200)}
                ></tr-button-small>
              </x-flex>
            `,
          columnWidth: 'max-content',
        },
      ],
    } satisfies DataViewOptions<typeof Workshop>);

    return html`
      <x-flex space="xl">
        <x-flex space="md">
          <x-flex space-between space="md" wrap horizontal crossaxis-center>
            <tr-h4> ${this._translation.translate('workshop', headingTranslationKey)} </tr-h4>
          </x-flex>
          <x-filter-editor .options=${filterOptions}></x-filter-editor>
          <x-data-view .options=${dataViewOptions}> </x-data-view>
        </x-flex>
      </x-flex>
    `;
  }
  //endregion

  renderContracts() {
    return html`
      <bh-page-profile-contracts-lecturer lecturer-id=${this.lecturerID.value}></bh-page-profile-contracts-lecturer>
    `;
  }

  //region comments
  private _newCommentElement = createRef<XInput>();
  private _lecturerComments(lecturerID: string, institutionID: string) {
    let newComment = '';
    const comments = this._lecturerCommentsController.value || [];
    return html`
      <!-- Comment history -->
      <x-flex space="xs">
        ${repeat(
          comments,
          comment => comment._id,
          comment => html`
            <x-flex
              horizontal
              crossaxis-center
              space-between
              space="md"
              ${css({
                borderRadius: this.sizes.borderRadiusSecondary,
              })}
            >
              <x-text flex> ${comment.text || 'Kein Text'} </x-text>
              <x-text tone="subtle">
                ${comment.createdAt
                  ? format(comment.createdAt, 'dd.LL.yyyy HH:mm')
                  : this._translation.translate('lecturer', 'noDate')}
              </x-text>
              <x-flex horizontal crossaxis-center space="xxs">
                <tr-button-small
                  icon="delete"
                  .color=${this.colors.tone.error}
                  @click=${() => this._deleteComment(comment, lecturerID, institutionID)}
                ></tr-button-small>
              </x-flex>
            </x-flex>
          `,
        )}
      </x-flex>

  <!-- Input new comment -->
  <x-flex horizontal crossaxis-center space="sm">
          <x-input
            placeholder-mode="hide"
            flex
            placeholder="Kommentar"
            ${ref(this._newCommentElement)}
            @value-picked=${(e: ValueEvent<string>) => (newComment = e.detail.value)}
            @submit=${(e: KeyboardEvent) =>
              this._addComment({ text: newComment, lecturerID: lecturerID, institutionID: institutionID })}
          ></x-input>
          <x-button mode="icon" icon="send" @click=${() =>
            this._addComment({ text: newComment, lecturerID: lecturerID, institutionID: institutionID })}></x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private async _addComment({ text, lecturerID, institutionID }) {
    if (!text || !lecturerID || !institutionID) return;
    try {
      this._newCommentElement.value?.value.next('');
      await TrDialog.waitFor(createLecturerComment(text, lecturerID, institutionID)());
      void XSnackbar.show('Kommentar erfolgreich hinzugefügt.');
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert('Es gab ein Problem beim Hinzufügen eines Kommentars. Bitte versuchen Sie es später erneut.');
    }
  }

  private async _deleteComment(comment, lecturerID, institutionID) {
    if (!comment || !lecturerID || !institutionID) return;
    if (!(await TrDialog.confirm('Soll dieser Kommentar wirklich gelöscht werden?'))) return;

    try {
      await deleteLecturerCommentByID(comment._id)();
    } catch (err) {
      logger.error({ err });
      void TrDialog.alert('Es gab ein Problem beim Löschen des Kommentars. Bitte versuchen Sie es später erneut.');
    }
  }

  //endregion
}
